import React from 'react';
import { Box, Checkbox, FormControlLabel, alpha, useTheme } from '@mui/material';
import { borderRadius, getSemanticSurfaceTokens, layoutGap } from '../../../theme/theme';
import { BodyText, CaptionText } from '../typography';
import { InlineReadinessBadge, type ReadinessTone } from './WorkflowStepComponents';

interface ParameterCreationConsentCardProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  autoConfirmed?: boolean;
  requiredToContinue?: boolean;
  disabled?: boolean;
}

interface ConsentState {
  statusLabel: string;
  statusTone: ReadinessTone;
  title: string;
  description: string;
  color: string;
  background: string;
  border: string;
}

const ParameterCreationConsentCard: React.FC<ParameterCreationConsentCardProps> = ({
  checked,
  onChange,
  autoConfirmed = false,
  requiredToContinue = true,
  disabled = false,
}) => {
  const theme = useTheme();
  const tokens = getSemanticSurfaceTokens(theme);
  const descriptionId = React.useId();

  const state: ConsentState = autoConfirmed
    ? {
        statusLabel: 'Already available',
        statusTone: 'ready',
        title: 'Required parameters already exist',
        description:
          'All required PipeEX parameters are available in the active Revit model. No model update is needed.',
        color: tokens.status.success.text,
        background: alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.045 : 0.07),
        border: tokens.status.success.border,
      }
    : checked
      ? {
          statusLabel: 'Confirmed',
          statusTone: 'set',
          title: 'Create missing Revit parameters',
        description:
          'Confirmed — PipeEX will add any missing required parameters when you continue.',
        color: tokens.status.success.text,
        background: alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.045 : 0.07),
        border: tokens.status.success.border,
        }
      : requiredToContinue
        ? {
            statusLabel: 'Action required',
            statusTone: 'blocked',
             title: 'Create missing Revit parameters',
            description:
              'Confirm that PipeEX may add any missing required parameters to the active Revit model before you continue.',
            color: tokens.status.warning.text,
            background: alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.04 : 0.065),
            border: tokens.status.warning.border,
          }
        : {
            statusLabel: 'Optional',
            statusTone: 'optional',
             title: 'Create missing Revit parameters',
            description:
              'Enable this option only if you want PipeEX to create any missing required parameters when you continue. This will update the active Revit model.',
            color: theme.palette.text.secondary,
            background: alpha(
              theme.palette.background.paper,
              theme.palette.mode === 'light' ? 0.55 : 0.14
            ),
            border: tokens.border.strong,
          };

  const accessibleLabel = autoConfirmed
    ? 'Required parameters already exist'
    : 'Create missing Revit parameters';

  return (
    <FormControlLabel
      disabled={autoConfirmed || disabled}
      control={
        <Checkbox
          checked={Boolean(checked)}
          onChange={(event) => {
            if (autoConfirmed || disabled) {
              return;
            }
            onChange(event.target.checked);
          }}
          size="small"
          inputProps={{
            'aria-label': accessibleLabel,
            'aria-describedby': descriptionId,
          }}
          sx={{
            p: layoutGap.micro,
            mr: layoutGap.dense,
            color: autoConfirmed ? state.color : alpha(theme.palette.text.secondary, 0.65),
            '&.Mui-checked, &.Mui-disabled': { color: state.color },
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        />
      }
      label={
        <Box
          component="span"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
            alignItems: 'center',
            gap: layoutGap.row,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              columnGap: layoutGap.row,
              rowGap: layoutGap.micro,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <CaptionText component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
              {state.title}
            </CaptionText>
            <BodyText
              component="span"
              id={descriptionId}
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.3 }}
            >
              {state.description}
            </BodyText>
          </Box>
          <Box component="span" sx={{ justifySelf: { xs: 'start', sm: 'end' } }}>
            <InlineReadinessBadge label={state.statusLabel} tone={state.statusTone} compact />
          </Box>
        </Box>
      }
      sx={{
        m: 0,
        width: '100%',
        minHeight: 38,
        px: layoutGap.comfort,
        py: layoutGap.micro,
        alignItems: 'center',
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${state.border}`,
        backgroundColor: state.background,
        boxShadow: `inset 3px 0 0 ${state.color}`,
        cursor: autoConfirmed || disabled ? 'default' : 'pointer',
        transition: theme.transitions.create(['background-color', 'border-color']),
        '& .MuiFormControlLabel-label': {
          width: '100%',
          minWidth: 0,
        },
        '&.Mui-disabled': { opacity: 1 },
        '&:focus-within': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        ...(!(autoConfirmed || disabled) && {
          '&:hover': {
            backgroundColor: alpha(state.color, theme.palette.mode === 'light' ? 0.13 : 0.18),
          },
        }),
      }}
    />
  );
};

export default ParameterCreationConsentCard;
